import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { getRestorableWindowPosition, saveWindowScrollPosition } from '../../../utils/scrollRestoration';

const RESTORE_TIMEOUT_MS = 2500;
const RESTORE_MIN_DURATION_MS = 400;
const RESTORE_STABLE_FRAMES = 3;

type PendingRestore = {
  pathId: string;
  target: number;
  startedAt: number;
};

function ScrollRestoration(): null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationRef = useRef(location);
  const pendingRestoreRef = useRef<PendingRestore | null>(null);

  locationRef.current = location;

  useEffect(() => {
    let frameId = 0;

    const saveCurrentPosition = (): void => {
      frameId = 0;
      if (pendingRestoreRef.current) {
        return;
      }
      saveWindowScrollPosition(locationRef.current, window.scrollY);
    };

    const handleScroll = (): void => {
      if (frameId) {
        return;
      }
      frameId = window.requestAnimationFrame(saveCurrentPosition);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const pathId = `${location.pathname}${location.search}`;
    const savedPosition = getRestorableWindowPosition(location, navigationType);
    const carriedOver = pendingRestoreRef.current?.pathId === pathId
      ? pendingRestoreRef.current
      : null;
    const target = savedPosition !== undefined ? savedPosition : carriedOver?.target;

    if (target === undefined) {
      pendingRestoreRef.current = null;
      return undefined;
    }

    const startedAt = carriedOver?.startedAt || Date.now();
    pendingRestoreRef.current = { pathId, target, startedAt };

    let frameId = 0;
    let stableFrames = 0;
    let isStopped = false;

    const stop = (): void => {
      isStopped = true;
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    const complete = (): void => {
      pendingRestoreRef.current = null;
      stop();
    };

    const step = (): void => {
      if (isStopped) {
        return;
      }

      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const nextY = Math.min(target, maxScroll);

      if (Math.abs(window.scrollY - nextY) > 1) {
        window.scrollTo(0, nextY);
      }

      const elapsed = Date.now() - startedAt;
      stableFrames = maxScroll >= target ? stableFrames + 1 : 0;

      const isSettled = stableFrames >= RESTORE_STABLE_FRAMES && elapsed >= RESTORE_MIN_DURATION_MS;
      if (isSettled || elapsed >= RESTORE_TIMEOUT_MS) {
        complete();
        return;
      }

      frameId = window.requestAnimationFrame(step);
    };

    frameId = window.requestAnimationFrame(step);
    window.addEventListener('wheel', complete, { passive: true });
    window.addEventListener('touchmove', complete, { passive: true });
    window.addEventListener('keydown', complete);

    return () => {
      stop();
      window.removeEventListener('wheel', complete);
      window.removeEventListener('touchmove', complete);
      window.removeEventListener('keydown', complete);
    };
  }, [location, navigationType]);

  return null;
}

export default ScrollRestoration;
